import { GraduationCap, Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useTenantAuthConfig } from '@/hooks/use-auth-config'
import { useAuth } from '@/lib/auth'
import { generatePkce } from '@/lib/pkce'
import { useTenant } from '@/lib/tenant'

export default function TenantLoginPage() {
  const tenant = useTenant()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirect') || `/${tenant.slug}/dashboard`

  const { data: authConfig, isLoading, error } = useTenantAuthConfig(tenant.slug)

  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true })
    }
  }, [isAuthenticated, navigate, redirectTo])

  const handleAzureLogin = async () => {
    const cfg = authConfig?.authConfig
    if (!cfg?.clientId || !cfg?.tenantId) {
      console.error('Azure AD configuration missing')
      return
    }

    // Generate PKCE code_verifier and code_challenge
    const { codeVerifier, codeChallenge } = await generatePkce()
    sessionStorage.setItem('pkce_code_verifier', codeVerifier)

    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`)
    const state = encodeURIComponent(`${tenant.slug}:${redirectTo}`)
    const params = [
      `client_id=${cfg.clientId}`,
      'response_type=code',
      `redirect_uri=${redirectUri}`,
      'scope=openid%20profile%20email',
      `state=${state}`,
      'response_mode=query',
      `code_challenge=${codeChallenge}`,
      'code_challenge_method=S256',
    ].join('&')

    window.location.href = `https://login.microsoftonline.com/${cfg.tenantId}/oauth2/v2.0/authorize?${params}`
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>エラー</CardTitle>
            <CardDescription>テナント情報の取得に失敗しました</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50">
      <div className="mb-8 flex items-center space-x-2">
        <GraduationCap className="h-12 w-12 text-emerald-600" />
        <h1 className="text-3xl font-bold">{authConfig?.name ?? tenant.name}</h1>
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>ログイン</CardTitle>
          <CardDescription>組織アカウントでログインしてください</CardDescription>
        </CardHeader>
        <CardContent>
          {authConfig?.authProvider === 'azure-ad' && (
            <Button onClick={handleAzureLogin} className="w-full" size="lg">
              Microsoft アカウントでログイン
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
